import { useState } from "react";
import { EngagementService } from "@services/engagements/engagementService";
import { DeliveryService, type MailSend } from "@services/delivery/deliveryService";
import { barGeometry, type Window } from "./scheduleTree";
import type { AxisTick } from "./scheduleTree";

/*
    Mail sends on the schedule.

    Rachel asked for a future delivery date "ideally on the schedule tab", and she is right
    that it belongs here: a send is a dated thing the campaign will do, and the schedule is
    the one screen that answers "what is going out, and when". Until now it did not know mail
    existed — the date lived only on the Direct Mail card, so a campaign posting 1,800 cards
    on the 1st looked, from here, like a campaign doing nothing.

    WHY THESE ARE NOT ScheduleEntry ROWS. A flight is a window during which a network may
    serve; a send is a batch somebody costed and approved, which posts once. Folding sends
    into the schedule tree would make every consumer of that tree handle a row with money
    attached, and would give a send a start and an end it does not have.

    WHAT THE BAR MEANS. It spans the day it posts to the day it should land, because mail is
    the one channel where those are different questions and the gap is a week. The right-hand
    end is an estimate and says so; nothing about a postcard arrives on a promised date.
*/

/** A send, flattened against the campaign it belongs to. */
export interface MailSendRow {
  launchId: number;
  launchName: string;
  send: MailSend;
  /** The day it posts. */
  start: Date;
  /** About when it lands. */
  end: Date;
}

const dayStart = (iso: string) => {
  // Parsed as local midnight. `new Date("2026-10-01")` is UTC midnight, which renders as the
  // previous day for anyone west of Greenwich — a send would sit one column early.
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

const fmt = (date: Date) =>
  date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** Batches worth a row. A cancelled send is not going anywhere. */
const WORTH_SHOWING = new Set(["awaiting_approval", "approved", "sending", "sent"]);

const STATE_TONE: Record<string, string> = {
  awaiting_approval: "#f59e0b",
  approved: "#10b981",
  sending: "#3b82f6",
  sent: "#6366f1",
};

/**
 * Every mail send across the engagement's campaigns.
 *
 * Failures are swallowed per campaign rather than per engagement: a campaign with mail
 * switched off answers with nothing useful, and one that errors must not blank the schedule,
 * which is somebody else's screen for reasons unrelated to mail.
 */
export async function loadMailSendRows(engagementId: number): Promise<MailSendRow[]> {
  const delivery = new DeliveryService();
  let campaigns: { id: number; name?: string | null }[] = [];
  try {
    campaigns = await new EngagementService().getEngagementLaunches(engagementId);
  } catch {
    return [];
  }

  const perCampaign = await Promise.all(
    campaigns.map(async (campaign) => {
      try {
        const { sends } = await delivery.listSends(campaign.id);
        return sends
          .filter((send) => WORTH_SHOWING.has(send.state))
          .map((send) => {
            // An undated send goes with the next publish, so it is drawn from today: the
            // honest answer to "when does this go" is "as soon as the campaign publishes".
            const start = send.scheduled_date ? dayStart(send.scheduled_date) : new Date();
            start.setHours(0, 0, 0, 0);
            const end = send.estimated_delivery ? dayStart(send.estimated_delivery) : start;
            return {
              launchId: campaign.id,
              launchName: campaign.name || "Campaign",
              send,
              start,
              end: end < start ? start : end,
            };
          });
      } catch {
        return [];
      }
    }),
  );
  return perCampaign.flat();
}

interface MailSendRowsProps {
  rows: MailSendRow[];
  window: Window;
  ticks: AxisTick[];
  monthLines: number[];
  showDayLines: boolean;
  onChanged: () => void | Promise<void>;
}

export default function MailSendRows({
  rows, window: chartWindow, ticks, monthLines, showDayLines, onChanged,
}: MailSendRowsProps) {
  /** The send whose date is being changed, if any. Dates change in a field, never by drag. */
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  if (rows.length === 0) return null;

  const reschedule = async (row: MailSendRow, date: string) => {
    setBusy(true);
    try {
      await new DeliveryService().updateSend(row.launchId, row.send.id, {
        scheduled_date: date || null,
      });
      setEditing(null);
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {rows.map((row) => {
        const geo = barGeometry({ start: row.start, end: row.end }, chartWindow);
        const color = STATE_TONE[row.send.state] ?? "#94a3b8";
        // Only a batch that has not reached the printer can move. Offering a date on one
        // already with Lob would be a control that quietly does nothing.
        const movable = row.send.is_open;

        return (
          <div key={`mail-${row.send.id}`} className="sched-grid sched-row sched-row--d1">
            <div className="sched-row__name">
              <i className="bi bi-envelope" style={{ color: "#5b3df5" }} />
              <span className="sched-row__text">
                {row.send.name || "Mail send"}
                <span className="text-muted"> · {row.launchName}</span>
              </span>
            </div>

            <div
              className="sched-row__dates"
              title={`${row.send.estimated_pieces.toLocaleString()} pieces, about $${Number(
                row.send.estimated_cost,
              ).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
            >
              {editing === row.send.id ? (
                <input
                  type="date"
                  className="form-control form-control-sm"
                  autoFocus
                  disabled={busy}
                  defaultValue={row.send.scheduled_date ?? ""}
                  aria-label={`Send date for ${row.send.name || "mail send"}`}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => reschedule(row, e.target.value)}
                  onBlur={() => setEditing(null)}
                />
              ) : (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  disabled={!movable}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(row.send.id);
                  }}
                >
                  {row.send.scheduled_date ? fmt(row.start) : "With next publish"}
                </button>
              )}
            </div>

            <div
              className="sched-track"
              style={
                showDayLines
                  ? {
                      backgroundImage:
                        "linear-gradient(to right, #eef2f7 1px, transparent 1px)",
                      backgroundSize: `${100 / chartWindow.totalDays}% 100%`,
                    }
                  : undefined
              }
            >
              {ticks.map((tick, i) => (
                <span
                  key={`t${i}`}
                  className="sched-track__line"
                  style={{ left: `${tick.leftPct}%` }}
                />
              ))}
              {monthLines.map((left, i) => (
                <span
                  key={`m${i}`}
                  className="sched-track__line sched-track__line--month"
                  style={{ left: `${left}%` }}
                />
              ))}
              <span
                className="sched-bar"
                style={{
                  left: `${geo.leftPct}%`,
                  width: `${geo.widthPct}%`,
                  background: color,
                }}
                title={`Posts ${fmt(row.start)}, arrives about ${fmt(row.end)} · ${
                  row.send.state_label
                }`}
              />
            </div>

            <span className="sched-row__go" aria-hidden="true" />
          </div>
        );
      })}
    </>
  );
}
